import type { Module } from "./types";
import { logger } from "../lib/logger";

// ─── Runtime module registry ────────────────────────────────
// Called once at app boot. The web app does:
//   import { registerModules } from "@retailos/core";
//   import jewelry from "@retailos/module-jewelry";
//   registerModules([jewelry]);
// After this point, getModule() / listModules() return the registered set.

const REGISTRY = new Map<string, Module>();

export function registerModules(modules: Module[]): void {
  for (const mod of modules) {
    if (REGISTRY.has(mod.id)) {
      logger.warn("Module already registered, skipping", { moduleId: mod.id });
      continue;
    }
    validateModule(mod);
    REGISTRY.set(mod.id, mod);
    logger.info("Registered module", {
      moduleId: mod.id,
      version: mod.version,
      capabilityCount: mod.capabilities.length,
      routeCount: mod.routes?.length ?? 0,
    });
  }
}

export function getModule(id: string): Module | undefined {
  return REGISTRY.get(id);
}

export function listModules(): Module[] {
  return Array.from(REGISTRY.values());
}

/** Return the subset of registered modules enabled for a given organization. */
export function getActiveModules(enabledIds: string[]): Module[] {
  return enabledIds
    .map((id) => REGISTRY.get(id))
    .filter((m): m is Module => m !== undefined);
}

/** Merge all nav items from active modules, sorted by group then order. */
export function collectNavItems(enabledIds: string[]) {
  const items = getActiveModules(enabledIds).flatMap((m) => m.navItems ?? []);
  return items.sort(
    (a, b) => a.group.localeCompare(b.group) || a.order - b.order,
  );
}

/** Merge all capabilities contributed by active modules. */
export function collectCapabilities(enabledIds: string[]) {
  return getActiveModules(enabledIds).flatMap((m) => m.capabilities);
}

function validateModule(mod: Module): void {
  if (!/^[a-z][a-z0-9_-]*$/.test(mod.id)) {
    throw new Error(
      `Module id "${mod.id}" must be lowercase kebab/snake_case starting with a letter`,
    );
  }
  const seenCaps = new Set<string>();
  for (const cap of mod.capabilities) {
    if (seenCaps.has(cap.id)) {
      throw new Error(`Duplicate capability "${cap.id}" in module "${mod.id}"`);
    }
    if (!cap.id.startsWith(`${mod.id}.`) && cap.module !== "core") {
      throw new Error(
        `Capability "${cap.id}" must be namespaced under module id "${mod.id}" or core`,
      );
    }
    seenCaps.add(cap.id);
  }
}
